<template>
  <ModalDialog
    :header-text="
      mode == UserModalMode.Add ? 'Add User' : `Modify User ${user?.UserName}`
    "
    :canBeClosed="!userSaving"
    contentClass="user-modal-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <form @submit.prevent="saveUserAsync" class="user-modal-content__user">
        <div class="form-control">
          <label for="user-name" data-cy="user-name-label">User Name</label>
          <input
            type="text"
            id="user-name"
            v-model="userName"
            :disabled="mode == UserModalMode.Edit || user?.IsOuAssignedAdmin"
            required
            data-cy="user-name"
          />
          <p v-if="!userNameValid" class="help-text error">
            User name does not allow spaces and most special characters - @ and
            . allowed.
          </p>
          <p v-if="userNameOnlyNumeric" class="help-text error">
            User name cannot be only numeric.
          </p>
          <span class="help-text"
            >Note that new account logins are no longer created with an
            automatic numeric prefix.</span
          >
        </div>
        <div class="form-control">
          <label for="email" data-cy="user-email-label">Email</label>
          <input
            type="email"
            id="email"
            v-model="email"
            :disabled="user?.IsOuAssignedAdmin"
            required
            data-cy="user-email"
          />
        </div>
        <div class="form-control">
          <label for="user-role">User Role</label>
          <div class="form-control__dropdown">
            <DropDownList
              id="user-role"
              :data-items="roleOptions"
              text-field="text"
              data-item-key="text"
              :popup-settings="{ className: 'modal-popup' }"
              v-model="userRoleOption"
              :disabled="user?.IsOuAssignedAdmin"
              data-cy="role-dropdown"
            />
            <div class="form-control__dropdown-parameters">
              <div
                v-for="parameter in userRoleOption.parameters"
                :key="parameter.name"
              >
                <input
                  type="checkbox"
                  :id="parameter.name"
                  v-model="parameter.value"
                  :disabled="user?.IsOuAssignedAdmin"
                />
                <label :for="parameter.name">{{ parameter.text }}</label>
              </div>
            </div>
            <label class="form-control__dropdown-label">
              {{ userRoleOption.label }}
            </label>
          </div>
        </div>
        <Button
          v-if="!userSaving"
          type="submit"
          theme-color="success"
          :disabled="saveDisabled"
          data-cy="save-user"
          >Save</Button
        >
        <Button v-else type="button" theme-color="success">
          <Loader
            class="user-saving-loader"
            size="large"
            type="pulsing"
            theme-color="light"
        /></Button>
      </form>
      <div class="user-modal-content__plants">
        <PlantGrid
          :plants="plants"
          :plantsLoading="plantsLoading"
          :includeFuturePlantsDefault="futurePlants"
          :editable="!props.user?.IsOuAssignedAdmin"
          @includeFuturePlantsChanged="onIncludeFuturePlantsChanged"
        />
        <div v-if="user && user.LastLoginDate" class="last-login">
          <span
            >Last login
            {{ new Date(user.LastLoginDate).toLocaleString() }}</span
          >
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import type { Facility } from "@/Domain/Facility";
import type { MemberShipUserPlus } from "@/Domain/MemberShipUserPlus";
import { UserModalMode } from "@/Domain/UserModalMode";
import { useMainStore } from "@/stores/useMainStore";
import { onBeforeMount, ref, computed } from "vue";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import PlantGrid from "./PlantGrid.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { Loader } from "@progress/kendo-vue-indicators";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

interface RoleOption {
  id: string;
  text: string;
  parameters: {
    name: string;
    text: string;
    value: boolean;
  }[];
  getRole: () => string;
  isUserRole: (role: string) => boolean;
  label?: string;
}

const main = useMainStore();
const notificationStore = useNotificationStore();

const emit = defineEmits<{
  (e: "closeModal", userSaved: boolean): void;
}>();

const props = defineProps<{
  mode: UserModalMode;
  user?: MemberShipUserPlus | null;
}>();

const plants = ref<Facility[]>([]);
const futurePlants = ref<boolean>(false);
const userName = ref<string>("");
const email = ref<string>("");
const plantsLoading = ref<boolean>(false);
const userSaving = ref<boolean>(false);

const roles = {
  Admin: "Customer",
  AdminNewUsers: "Customer_Admin",
  ExternalCustomerLocSchedABIAPA: "LOC_SchedA&B_IA_PA",
  ExternalCustomerLoc: "LOC",
  ExternalCustomerSchedAB: "ScheduleA&B",
  ExternalCustomerSchedABIA: "ScheduleA&B&IA",
  ExternalCustomerSchedABPA: "SchedA&B_PA",
  ExternalCustomerLocSchedAB: "LOC_ScheduleA&B",
  ExternalCustomerSchedABIAPA: "SchedA&B_IA_PA",
  ExternalCustomerLocSchedABIA: "LOC_ScheduleA&B&IA",
  ExternalCustomerLocSchedABPA: "LOC_SchedA&B_PA",
  ExternalCustomerFinancial: "Financial",
  UkdApplication: "UKD_Application_Trademarks",
};

const userNameRegex = () => /^[a-zA-Z0-9@.]+$/g;
const userNameOnlyNumericRegex = () => /^[0-9]+$/g;

const userNameValid = computed(() => {
  return (
    props.mode == UserModalMode.Edit ||
    props.user?.IsOuAssignedAdmin ||
    !userName.value ||
    userNameRegex().test(userName.value)
  );
});

const userNameOnlyNumeric = computed(() => {
  return (
    props.mode != UserModalMode.Edit &&
    userNameOnlyNumericRegex().test(userName.value)
  );
});

const saveDisabled = computed(() => {
  return (
    plantsLoading.value ||
    !userRoleOption.value ||
    userRoleOption.value.getRole() === "" ||
    !userName.value ||
    !email.value ||
    props.user?.IsOuAssignedAdmin ||
    !userNameValid.value ||
    // #31045: username cannot be only numeric
    userNameOnlyNumeric.value ||
    (requirePlantSelection.value &&
      plants.value.filter((x) => x.IsAuthorized).length === 0)
  );
});

const requirePlantSelection = computed(() => {
  return (
    userRoleOption.value &&
    userRoleOption.value.id != "Ukd" &&
    userRoleOption.value.id != "Financials"
  );
});

const roleOptions = computed<RoleOption[]>(() => [
  {
    id: "Default",
    text: "Select a role",
    parameters: [],
    getRole() {
      return "";
    },
    isUserRole() {
      return false;
    },
  },
  {
    id: "Admin",
    text: "Admin",
    parameters: [
      {
        name: "IsAdmin",
        text: "With ability to add new OUDirect users?",
        value: props.user?.Roles.includes(roles.AdminNewUsers) ?? false,
      },
    ],
    getRole() {
      return this.parameters[0].value ? roles.AdminNewUsers : roles.Admin;
    },
    isUserRole(role: string) {
      return role == roles.Admin || role == roles.AdminNewUsers;
    },
  },
  {
    id: "Customized",
    text: "Customized",
    parameters: [
      {
        name: "KosherCertificates",
        text: "Kosher Certificates",
        value:
          props.user?.Roles.some((r) =>
            [
              roles.ExternalCustomerLoc,
              roles.ExternalCustomerLocSchedABIAPA,
              roles.ExternalCustomerLocSchedAB,
              roles.ExternalCustomerLocSchedABIA,
              roles.ExternalCustomerLocSchedABPA,
            ].includes(r)
          ) ?? false,
      },
      {
        name: "ViewIngredientProductLists",
        text: "View Ingredient & Product Lists",
        value:
          props.user?.Roles.some((r) =>
            [
              roles.ExternalCustomerLocSchedABIAPA,
              roles.ExternalCustomerLocSchedAB,
              roles.ExternalCustomerLocSchedABIA,
              roles.ExternalCustomerLocSchedABPA,
              roles.ExternalCustomerSchedAB,
              roles.ExternalCustomerSchedABPA,
              roles.ExternalCustomerSchedABIAPA,
            ].includes(r)
          ) ?? false,
      },
      {
        name: "UpdateIngredientList",
        text: "Update Ingredient List",
        value:
          props.user?.Roles.some((r) =>
            [
              roles.ExternalCustomerLocSchedABIAPA,
              roles.ExternalCustomerLocSchedABIA,
              roles.ExternalCustomerSchedABIA,
              roles.ExternalCustomerSchedABIAPA,
            ].includes(r)
          ) ?? false,
      },
      {
        name: "UpdateProductList",
        text: "Update Product List",
        value:
          props.user?.Roles.some((r) =>
            [
              roles.ExternalCustomerLocSchedABIAPA,
              roles.ExternalCustomerLocSchedABPA,
              roles.ExternalCustomerSchedABPA,
              roles.ExternalCustomerSchedABIAPA,
            ].includes(r)
          ) ?? false,
      },
    ],
    getRole() {
      // All checked
      if (this.parameters.every((p) => p.value))
        return roles.ExternalCustomerLocSchedABIAPA;
      // Only first checked and others unchecked
      if (
        this.parameters[0].value &&
        !this.parameters[1].value &&
        !this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerLoc;
      // Only second checked and others unchecked
      if (
        !this.parameters[0].value &&
        this.parameters[1].value &&
        !this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerSchedAB;
      // Only third checked and others unchecked
      if (
        !this.parameters[0].value &&
        !this.parameters[1].value &&
        this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABIA;
      // Only fourth checked and others unchecked
      if (
        !this.parameters[0].value &&
        !this.parameters[1].value &&
        !this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABPA;
      // First and second checked and others unchecked
      if (
        this.parameters[0].value &&
        this.parameters[1].value &&
        !this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedAB;
      // Second and third checked and others unchecked
      if (
        !this.parameters[0].value &&
        this.parameters[1].value &&
        this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABIA;
      // Third and fourth checked and others unchecked
      if (
        !this.parameters[0].value &&
        !this.parameters[1].value &&
        this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABIAPA;
      // First and third checked and others unchecked
      if (
        this.parameters[0].value &&
        !this.parameters[1].value &&
        this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedABIA;
      // Second and fourth checked and others unchecked
      if (
        !this.parameters[0].value &&
        this.parameters[1].value &&
        !this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABPA;
      // First and fourth checked and others unchecked
      if (
        this.parameters[0].value &&
        !this.parameters[1].value &&
        !this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedABPA;
      // First three checked and fourth unchecked
      if (
        this.parameters[0].value &&
        this.parameters[1].value &&
        this.parameters[2].value &&
        !this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedABIA;
      // Last three checked and first unchecked
      if (
        !this.parameters[0].value &&
        this.parameters[1].value &&
        this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerSchedABIAPA;
      // All checked except second
      if (
        this.parameters[0].value &&
        !this.parameters[1].value &&
        this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedABIAPA;
      // All checked except third
      if (
        this.parameters[0].value &&
        this.parameters[1].value &&
        !this.parameters[2].value &&
        this.parameters[3].value
      )
        return roles.ExternalCustomerLocSchedABPA;
      return roles.ExternalCustomerLoc;
    },
    isUserRole(role: string) {
      return (
        role == roles.ExternalCustomerLoc ||
        role == roles.ExternalCustomerSchedAB ||
        role == roles.ExternalCustomerSchedABIA ||
        role == roles.ExternalCustomerSchedABPA ||
        role == roles.ExternalCustomerLocSchedAB ||
        role == roles.ExternalCustomerSchedABIAPA ||
        role == roles.ExternalCustomerLocSchedABIA ||
        role == roles.ExternalCustomerLocSchedABPA ||
        role == roles.ExternalCustomerLocSchedABIAPA
      );
    },
  },
  {
    id: "Financials",
    text: "Financial (Company)",
    parameters: [],
    getRole() {
      return roles.ExternalCustomerFinancial;
    },
    isUserRole(role: string) {
      return role == roles.ExternalCustomerFinancial;
    },
  },
  {
    id: "Ukd",
    text: "UKD",
    parameters: [],
    getRole() {
      return roles.UkdApplication;
    },
    isUserRole(role: string) {
      return role == roles.UkdApplication;
    },
    label: "UKD, Application Forms and Trademarks Only",
  },
]);

const userRoleOption = ref<RoleOption>(roleOptions.value[0]);

const getFacilitiesAsync = async function () {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    includeMainOffice: true,
    subApplication: 10,
    contactId: -1,
    includeSpecial: false,
  };
  return CacheService.getResponseAsync(
    createCacheKey(requestParams, "GetFacilities"),
    () => api.get("GetFacilities", requestParams)
  );
};

const getUserPlantsPermissionsAsync = async function () {
  return api.get("GetUserPlantsPermissions", {
    loginName: props.user!.UserName,
    startsWith: false,
  });
};

const updatePlantsAsync = async function () {
  plantsLoading.value = true;
  await handleResponsePromiseAsync(
    getFacilitiesAsync(),
    (data) => {
      plants.value = data.filter((p) => p.FacilityType == 0);
    },
    "UserModal.vue",
    "updatePlantsAsync"
  );
  if (props.mode == UserModalMode.Edit) {
    await handleResponsePromiseAsync(
      getUserPlantsPermissionsAsync(),
      (data) => {
        const permissions = data.map((x) => x.PlantID);
        futurePlants.value = permissions.includes("*");
        plants.value.forEach((plant) => {
          plant.IsAuthorized =
            permissions.includes("*") ||
            permissions.includes(plant.OwnsId.toString());
        });
      },
      "UserModal.vue",
      "updatePlantsAsync"
    );
  }
  plantsLoading.value = false;
};

const onIncludeFuturePlantsChanged = function (value: boolean) {
  futurePlants.value = value;
  if (value) {
    // When "Include Future Plants" is checked, grant access to all current plants as well
    plants.value.forEach((plant) => {
      plant.IsAuthorized = true;
    });
  }
};

const saveUserAsync = async function () {
  userSaving.value = true;

  await handleResponsePromiseAsync(
    api.post("SaveUser", {
      UserName: userName.value,
      CompId: main.companyLogin,
      Email: email.value,
      Role: userRoleOption.value.getRole(),
      IsNewUser: props.mode === UserModalMode.Add,
      SelectedPlantIds:
        plants.value
          .filter((x) => x.IsAuthorized)
          .map((x) => x.OwnsId.toString()) || [],
      IncludeFuturePlants: futurePlants.value,
    }),
    (data) => {
      if (data) {
        notificationStore.notify({
          type: NotificationTypeEnum.success,
          message: "User saved successfully",
          duration: 5000,
        });
        closeModal(true, true);
      }
    },
    "UserModal.vue",
    "saveUserAsync",
    (error, response) => {
      if (response?.error) {
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: response.error.toString(),
          duration: 5000,
        });
        return;
      }
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error saving user: ${error}`,
        duration: 5000,
      });
    }
  );
  userSaving.value = false;
};

const closeModal = function (closed: boolean, userSaved: boolean = false) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while user is being saved",
      duration: 5000,
    });
    return;
  }
  emit("closeModal", userSaved);
};

onBeforeMount(() => {
  if (props.mode === UserModalMode.Edit) {
    userName.value = props.user!.UserName;
    email.value = props.user!.Email;
    if (props.user!.Roles.length > 0) {
      userRoleOption.value = roleOptions.value.find((opt) =>
        props.user!.Roles.some((r) => opt.isUserRole(r))
      ) as RoleOption;
    }
  } else {
    userName.value = "";
    email.value = "";
    futurePlants.value = false;
    userRoleOption.value = roleOptions.value[0];
  }
  updatePlantsAsync();
});
</script>

<style lang="scss">
.user-modal-content {
  display: flex;
  gap: 12px;

  &__user {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 8px;

    .form-control {
      display: flex;
      flex-direction: column;
      gap: 4px;

      label {
        font-weight: 600;
      }

      .help-text {
        display: block;
        font-size: 10px;
        color: #999;
        margin: 0 0 0 0.25rem;

        &.error {
          color: #f00;
        }
      }

      &__dropdown-parameters {
        margin-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
    }
  }

  &__plants {
    .plant-grid {
      max-width: 600px;
      min-height: 200px;
      max-height: 430px;
    }
  }

  .user-saving-loader {
    padding: 0;
  }

  .last-login {
    text-align: right;
    margin-top: 12px;

    span {
      color: #999;
    }
  }
}
</style>
